import { useEffect } from 'react';
import {
  updateSEO,
  setCanonicalLink,
  setLanguageAlternates,
  addBreadcrumbStructuredData,
  addDateCalculatorHowToSchema
} from '../utils/seoUtils';

// ✅ datePublished 保留原始發佈日（不可修改）
// ✅ dateModified 更新為 2026-05-01
const DATE_MODIFIED  = '2026-05-01';
const DATE_PUBLISHED = '2023-06-01';

const BASE_URL = 'https://lovedays.homy.hk/';

const ROUTE_METADATA = {
  '/about': {
    zh: {
      title: '關於我們｜戀愛紀念日計算器',
      description: '了解戀愛紀念日計算器的團隊與使命，以及我們如何透過簡單實用的數位工具幫助情侶記錄重要時刻。',
      keywords: '關於我們,戀愛紀念日計算器,愛情里程碑,數位工具'
    },
    en: {
      title: 'About Us | Love Anniversary Calculator',
      description: 'Learn about the team and mission behind the Love Anniversary Calculator and our focus on simple tools for recording relationship milestones.',
      keywords: 'about us,love anniversary calculator,relationship milestones,digital tools'
    }
  },
  '/features': {
    zh: {
      title: '功能介紹｜戀愛紀念日計算器',
      description: '查看多種紀念日類型、精確日期計算、愛情成就、里程碑、分享功能與戀愛紀念日圖片生成等現有功能。',
      keywords: '戀愛紀念日功能,日期計算,愛情成就,愛情里程碑,紀念圖片'
    },
    en: {
      title: 'Features | Love Anniversary Calculator',
      description: 'Explore anniversary types, precise date calculations, love achievements, milestones, sharing tools, and anniversary image generation.',
      keywords: 'anniversary calculator features,date calculation,love achievements,milestones,anniversary images'
    }
  },
  '/help': {
    zh: {
      title: '使用幫助｜戀愛紀念日計算器',
      description: '查看戀愛紀念日、日期計算、自定義紀念類型、分享、愛情成就、里程碑與語言切換的使用說明。',
      keywords: '使用幫助,戀愛紀念日教學,日期計算器說明,愛情里程碑'
    },
    en: {
      title: 'Help Center | Love Anniversary Calculator',
      description: 'Find help for anniversary and date calculations, custom anniversary types, sharing, love achievements, milestones, and language switching.',
      keywords: 'help center,anniversary calculator help,date calculator guide,love milestones'
    }
  },
  '/contact': {
    zh: {
      title: '聯絡我們｜戀愛紀念日計算器',
      description: '如對戀愛紀念日計算器有意見、問題或建議，請透過頁面提供的電子郵件聯絡我們。',
      keywords: '聯絡我們,戀愛紀念日計算器,Homy Tech Limited,意見建議'
    },
    en: {
      title: 'Contact Us | Love Anniversary Calculator',
      description: 'Contact us by email with feedback, questions, or suggestions about the Love Anniversary Calculator.',
      keywords: 'contact us,love anniversary calculator,Homy Tech Limited,feedback'
    }
  },
  '/privacy': {
    zh: {
      title: '隱私政策｜戀愛紀念日計算器',
      description: '閱讀戀愛紀念日計算器的隱私政策，了解資訊收集與使用、Cookie、資料安全、資訊共享及使用者權利。',
      keywords: '隱私政策,資料安全,Cookie,個人資訊,戀愛紀念日計算器'
    },
    en: {
      title: 'Privacy Policy | Love Anniversary Calculator',
      description: 'Read the privacy policy covering information collection and use, cookies, data security, information sharing, and user rights.',
      keywords: 'privacy policy,data security,cookies,personal information,love anniversary calculator'
    }
  },
  '/terms': {
    zh: {
      title: '使用條款｜戀愛紀念日計算器',
      description: '閱讀戀愛紀念日計算器的使用條款，包括服務描述、使用者責任、知識產權、免責聲明、責任限制與適用法律。',
      keywords: '使用條款,服務條款,使用者責任,知識產權,戀愛紀念日計算器'
    },
    en: {
      title: 'Terms of Service | Love Anniversary Calculator',
      description: 'Read the terms covering the service, user responsibilities, intellectual property, disclaimers, liability, and governing law.',
      keywords: 'terms of service,user responsibilities,intellectual property,disclaimer,love anniversary calculator'
    }
  }
};

export const useSEO = ({ pathname = '/', language, activeTab, calculatorData = {} }) => {
  useEffect(() => {
    let title, description, additionalData = {};

    const routeMetadata = ROUTE_METADATA[pathname];
    if (routeMetadata) {
      const content = routeMetadata[language] || routeMetadata['zh'];
      const canonicalUrl = `${BASE_URL}${pathname.slice(1)}`;

      updateSEO({
        title: content.title,
        description: content.description,
        language,
        additionalData: { keywords: content.keywords },
        activeTab,
        calculatorData,
        canonicalUrl
      });
      setCanonicalLink(canonicalUrl);
      setLanguageAlternates({
        'zh-TW': canonicalUrl,
        'en':    canonicalUrl
      });
      return;
    }

    if (pathname !== '/') {
      const content = language === 'zh'
        ? {
            title: '404｜找不到此頁面｜戀愛紀念日計算器',
            description: '你要尋找的頁面不存在，或網址可能已變更。',
            keywords: '404,找不到頁面,頁面不存在'
          }
        : {
            title: '404 | Page Not Found | Love Anniversary Calculator',
            description: 'The page you are looking for does not exist or its address may have changed.',
            keywords: '404,page not found,page does not exist'
          };
      const canonicalPath = pathname.replace(/^\/+|\/+$/g, '');
      const canonicalUrl = `${BASE_URL}${canonicalPath}`;

      updateSEO({
        title: content.title,
        description: content.description,
        language,
        additionalData: { keywords: content.keywords },
        activeTab,
        calculatorData,
        canonicalUrl,
        robots: 'noindex,follow'
      });
      setCanonicalLink(canonicalUrl);
      setLanguageAlternates({
        'zh-TW': canonicalUrl,
        'en':    canonicalUrl
      });
      return;
    }

    if (activeTab === 'love') {
      const typeLabels = {
        couple: {
          zh: { title: '戀愛紀念日計算器 - 計算在一起的日子', desc: '計算你們在一起的天數，紀錄戀愛紀念日。專為情侶打造的紀念日計算工具，記錄愛情里程碑，慶祝你們的每一天。' },
          en: { title: 'Dating Anniversary Calculator - Days Together Counter', desc: 'Calculate days together with your significant other. Track your relationship milestones and celebrate each day of your journey together.' }
        },
        wedding: {
          zh: { title: '結婚紀念日計算器 - 婚姻時光計數器', desc: '計算你們結婚的天數、月數和年數。記錄婚姻生活的重要時刻，慶祝每一個結婚紀念日。' },
          en: { title: 'Wedding Anniversary Calculator - Marriage Day Counter', desc: 'Calculate days, months and years since your wedding. Track important marriage milestones and celebrate each anniversary.' }
        },
        'first-meet': {
          zh: { title: '相識紀念日計算器 - 緣分開始的日子', desc: '計算你們相識的天數，記錄第一次相遇的紀念日。追蹤你們相識以來的時光，慶祝這段關係的開始。' },
          en: { title: 'First Meeting Anniversary Calculator - When We First Met', desc: 'Calculate days since you first met. Track the time since your paths crossed and celebrate the beginning of your relationship.' }
        },
        'first-date': {
          zh: { title: '首次約會紀念日計算器 - 第一次約會的日子', desc: '計算你們第一次約會以來的天數。記錄這個特別的日子，慶祝你們感情旅程的開始。' },
          en: { title: 'First Date Anniversary Calculator - Our First Date Counter', desc: 'Calculate days since your first date. Remember this special day and celebrate the beginning of your romantic journey.' }
        },
        proposal: {
          zh: { title: '求婚紀念日計算器 - 紀念求婚的日子', desc: '計算從求婚那天到現在的日子。記錄這個重要的承諾時刻，慶祝你們邁向婚姻的重要里程碑。' },
          en: { title: 'Proposal Anniversary Calculator - Engagement Day Counter', desc: 'Calculate days since your proposal or engagement. Commemorate this important commitment and celebrate the milestone in your journey to marriage.' }
        }
      };

      if (calculatorData.anniversaryType && typeLabels[calculatorData.anniversaryType]) {
        const content = typeLabels[calculatorData.anniversaryType][language]
          || typeLabels[calculatorData.anniversaryType]['zh'];
        title = content.title;
        description = content.desc;
      } else {
        title = language === 'zh'
          ? '戀愛紀念日計算器 - 記錄你們的愛情時光'
          : 'Love Anniversary Calculator - Track Your Relationship Journey';
        description = language === 'zh'
          ? '計算你們在一起的天數，記錄戀愛紀念日，慶祝愛情里程碑和成就。讓每一天都充滿回憶和意義！支持相識、交往、結婚等多種紀念日類型。'
          : 'Calculate days together, track love milestones and achievements. Make every day meaningful in your relationship! Supports dating, wedding, first-met and other anniversary types.';
      }

      if (calculatorData.anniversaryDate) {
        const anniversaryType = calculatorData.anniversaryType || 'couple';
        additionalData = {
          dateCreated: DATE_PUBLISHED,
          dateModified: DATE_MODIFIED,
          keywords: language === 'zh'
            ? '戀愛紀念日,愛情計算器,情侶紀念日,愛情里程碑,愛情成就,相識紀念日,結婚紀念日,520天,1314天'
            : 'love anniversary,relationship calculator,couple days,love milestones,love achievements,dating anniversary,wedding anniversary,days together',
          applicationSubCategory: getSubcategory(anniversaryType, language)
        };
        addBreadcrumbStructuredData({ language, activeTab, relationshipType: anniversaryType });
      } else {
        addBreadcrumbStructuredData({ language, activeTab });
      }
    } else {
      if (calculatorData.calculationMode === 'workdays') {
        title = language === 'zh'
          ? '工作日計算器 - 計算工作日數量和日期差距'
          : 'Business Days Calculator - Count Working Days Between Dates';
        description = language === 'zh'
          ? '工作日以星期一至星期五為準，不計週末；目前不會自動扣除公眾假期。'
          : 'Workday calculations count Monday through Friday, exclude weekends, and do not automatically exclude public holidays.';
        addDateCalculatorHowToSchema({ language, calculationMode: 'workdays' });
      } else {
        title = language === 'zh'
          ? '日期計算器 - 計算日期間隔和工作日'
          : 'Date Calculator - Calculate Days Between Dates';
        description = language === 'zh'
          ? '計算兩個日期之間的天數、工作日和時間差距。實用的日期計算工具，幫助你安排和記錄重要日期，計算工作日、假期等。'
          : 'Calculate days, working days and time between any two dates. A useful tool for planning and tracking important dates, calculating business days, vacations and more.';
        addDateCalculatorHowToSchema({ language, calculationMode: 'all' });
      }
      addBreadcrumbStructuredData({ language, activeTab });
    }

    const staticPageContent = language === 'zh'
      ? {
          title: '戀愛紀念日計算器 - 記錄你們的愛情時光',
          description: '免費線上戀愛紀念日計算器，計算情侶在一起的天數、月數與年數，並追蹤重要愛情里程碑。',
          keywords: '戀愛紀念日,愛情計算器,情侶紀念日,愛情里程碑,相識紀念日,結婚紀念日,日期計算器'
        }
      : {
          title: 'Love Anniversary Calculator - Track Your Relationship Journey',
          description: 'Calculate days, months and years together and track meaningful relationship milestones with a free love anniversary calculator.',
          keywords: 'love anniversary,relationship calculator,couple days,love milestones,dating anniversary,wedding anniversary,date calculator'
        };

    if (activeTab === 'love' && !calculatorData.anniversaryType) {
      title = staticPageContent.title;
      description = staticPageContent.description;
      additionalData = {
        dateCreated: DATE_PUBLISHED,
        dateModified: DATE_MODIFIED,
        keywords: staticPageContent.keywords,
        applicationSubCategory: getSubcategory('couple', language)
      };
    }

    const canonicalUrl = BASE_URL;

    updateSEO({
      title,
      description,
      language,
      additionalData,
      activeTab,
      calculatorData,
      canonicalUrl
    });
    setCanonicalLink(canonicalUrl);

    const typeParam = calculatorData.anniversaryType && activeTab === 'love'
      ? `&type=${calculatorData.anniversaryType}` : '';
    const tabParam = activeTab === 'standard' ? '&tab=standard' : '';
    const modeParam = calculatorData.calculationMode === 'workdays' ? '&mode=workdays' : '';

    setLanguageAlternates({
      'zh-TW': `${BASE_URL}?lang=zh${tabParam}${modeParam}${typeParam}`,
      'en':    `${BASE_URL}?lang=en${tabParam}${modeParam}${typeParam}`
    });

  }, [pathname, language, activeTab, calculatorData]);
};

const getSubcategory = (anniversaryType, language) => {
  const subcategories = {
    couple:       language === 'zh' ? '戀愛紀念日計算器' : 'Dating Anniversary Calculator',
    wedding:      language === 'zh' ? '結婚紀念日計算器' : 'Wedding Anniversary Calculator',
    'first-meet': language === 'zh' ? '相識紀念日計算器' : 'First Meeting Anniversary Calculator',
    'first-date': language === 'zh' ? '首次約會紀念計算器' : 'First Date Anniversary Calculator',
    proposal:     language === 'zh' ? '求婚紀念日計算器' : 'Proposal Anniversary Calculator',
    custom:       language === 'zh' ? '自定義紀念日計算器' : 'Custom Anniversary Calculator'
  };
  return subcategories[anniversaryType] || subcategories.couple;
};

export default useSEO;